// src/pages/reports/SoldLeads.jsx
import React, { useMemo, useState } from 'react';
import {
  Heading,
  Text,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Checkbox,
  HStack,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

import ReportEditor from '../ReportEditor';
import useSoldLeads from '../../hooks/useSoldLeads';
import SoldLeadsTable from '../../components/soldLeads/SoldLeadsTable';
import SoldLeadsModals from '../../components/soldLeads/SoldLeadsModals';

export default function SoldLeads() {
  const navigate = useNavigate();

  // All data, mutations, and modal/editor state from the hook
  const {
    // data & paging
    rows, loading, cursor, loadingMore, loadMore,
    // selection & drawer
    selectedLeadId, setSelectedLeadId, editor,
    // forward modal
    forwardDlg, forwardRecipients, setForwardRecipients,
    forwardSubject, setForwardSubject, forwardNote, setForwardNote,
    savingForward, openForward, saveForward,
    // cancel sale modal
    cancelOpen, closeCancelSale, openCancelSale,
    reasonPreset, setReasonPreset, reasonText, setReasonText,
    submittingCancel, submitCancelSale,
    // mutations
    handleStageChange, saveLeadField, updateRowField, isSaving, savingStage,
    // utilities
    handleCopy, handleEmail,
  } = useSoldLeads();

  // Page-only filters
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');

  // NEW: "Hide Assigned" toggle (default ON, persisted)
  const LS_KEY = 'fixihub.sales.hideAssigned';
  const [hideAssigned, setHideAssigned] = useState(() => {
    const v = localStorage.getItem(LS_KEY);
    return v == null ? true : v === 'true';
  });
  const toggleHideAssigned = (checked) => {
    setHideAssigned(checked);
    try {
      localStorage.setItem(LS_KEY, String(checked));
    } catch {}
  };

  // Compute rows after applying "Hide Assigned"
  const visibleRows = useMemo(() => {
    if (!hideAssigned) return rows;
    const list = Array.isArray(rows) ? rows : [];
    return list.filter(
      (r) => String(r?.postSaleStage || '').toLowerCase() !== 'survey_assigned'
    );
  }, [rows, hideAssigned]);

  // Open full Admin Sales Report view for a lead
  function gotoSalesReport(leadId) {
    if (!leadId) return;
    navigate(`/reports/sales-report?leadId=${encodeURIComponent(leadId)}&back=/reports/sales`);
  }

  return (
    <>
      <Heading size="lg" color="blue.600" mb={2}>
        Sales — Ready to Survey
      </Heading>
      <Text color="gray.600" mb={3}>
        Open the report to review, then use the "Stage" and "Actions" to manage the Survey.
      </Text>

      {/* Small filter row: Hide Assigned */}
      <HStack justify="flex-end" mb={2}>
        <Checkbox
          colorScheme="green"
          isChecked={hideAssigned}
          onChange={(e) => toggleHideAssigned(e.target.checked)}
        >
          Hide Assigned
        </Checkbox>
      </HStack>

      {/* Filters + Table */}
      <SoldLeadsTable
        rows={visibleRows}
        loading={loading}
        cursor={cursor}
        loadingMore={loadingMore}
        search={search}
        setSearch={setSearch}
        stageFilter={stageFilter}
        setStageFilter={setStageFilter}
        onLoadMore={loadMore}
        onOpenSalesReport={gotoSalesReport}
        handlers={{
          handleStageChange,
          saveLeadField,
          updateRowField,
          isSaving,
          savingStage,
          handleCopy,
          handleEmail,
          openForward,
          openCancelSale,
        }}
      />

      {/* Drawer Editor */}
      <Drawer isOpen={editor.isOpen} placement="right" onClose={editor.onClose} size="xl">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Sales Report</DrawerHeader>
          <DrawerBody>
            {selectedLeadId && <ReportEditor leadId={selectedLeadId} onClose={editor.onClose} />}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Modals (Forward + Cancel) */}
      <SoldLeadsModals
        // Forward
        isForwardOpen={forwardDlg.isOpen}
        onForwardClose={forwardDlg.onClose}
        forwardRecipients={forwardRecipients}
        setForwardRecipients={setForwardRecipients}
        forwardSubject={forwardSubject}
        setForwardSubject={setForwardSubject}
        forwardNote={forwardNote}
        setForwardNote={setForwardNote}
        savingForward={savingForward}
        onSaveForward={saveForward}
        // Cancel
        isCancelOpen={cancelOpen}
        onCancelClose={closeCancelSale}
        reasonPreset={reasonPreset}
        setReasonPreset={setReasonPreset}
        reasonText={reasonText}
        setReasonText={setReasonText}
        submittingCancel={submittingCancel}
        onSubmitCancel={submitCancelSale}
      />
    </>
  );
}
